import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  Sparkles, 
  Brain, 
  MessageSquare, 
  Trophy, 
  GraduationCap, 
  Users, 
  Zap, 
  Shield,
  Target,
  TrendingUp
} from 'lucide-react';
import CalendlyPopup from '../components/CalendlyPopup';
import { useCalendlyAndVideo } from '../hooks/useCalendlyAndVideo';

const UpdatesPage = () => {
  const { isCalendlyOpen, openCalendly, closeCalendly } = useCalendlyAndVideo();
  
  const updateCategories = [
    {
      id: 'roleplay',
      icon: MessageSquare,
      title: 'Role Play Training Revolution',
      gradient: 'from-blue-500 to-purple-600',
      features: [
        {
          id: 'realtime-ai',
          title: 'Real-Time AI Role Play Conversations',
          date: 'Released: September - November 2025',
          description: 'Practice makes perfect. Our new Traain™-powered role play system provides immersive, real-time conversational training experiences.',
          highlights: [
            'Live AI Conversations — Engage in realistic role play scenarios with our advanced AI conversation engine (Oct 31, 2025)',
            'OpenAI SDP Session Integration — Cutting-edge real-time voice and text capabilities (Sep 25, 2025)',
            'AI-Created Role Plays — Generate custom role play scenarios automatically based on your training needs (Oct 7-16, 2025)',
            'Progress Tracking — Visual progress bars and performance badges keep trainees motivated (Nov 19-24, 2025)'
          ]
        },
        {
          id: 'gamification',
          title: 'Gamification & Leaderboards',
          date: 'Released: September - November 2025',
          description: 'Turn training into a competitive advantage with our new gamification features.',
          highlights: [
            'Performance Rankings — See how you stack up against your team with dynamic leaderboards (Nov 11, 2025)',
            'Moving Average Scoring — Fair, balanced scoring that rewards consistent improvement (Sep 23, 2025)',
            'Gamification Results Pages — Celebrate achievements with dedicated results dashboards (Nov 11, 2025)',
            'Chat Score Highlights — Review the exact moments that earned your scores (Oct 13-27, 2025)'
          ]
        },
        {
          title: 'Enhanced Role Play Analytics',
          date: 'Released: September 2025',
          highlights: [
            'Score Attribution — See exactly which parts of your conversation led to your scores',
            'Performance Metrics Collection — Comprehensive tracking of role play and training engagement (Sep 19, 2025)',
            'Team Performance Reports — Track your team&apos;s training progress in one unified view'
          ]
        }
      ]
    },
    {
      icon: GraduationCap,
      title: 'Training Platform Enhancements',
      gradient: 'from-purple-500 to-pink-600',
      features: [
        {
          title: 'Redesigned Training Experience',
          date: 'Released: November 27-29, 2025',
          description: 'A complete visual overhaul of the training system with our stunning new dark theme interface.',
          highlights: [
            'Aurora Voice Interface — Futuristic role play design with sleek animations (Nov 29, 2025)',
            'Traain™ Dark Theme — Modern, eye-friendly dark mode across all AI wizards and training tools',
            'Streamlined Wizards — Redesigned step-by-step wizards for creating training and role plays',
            'Build from Scratch or Use Templates — Flexible options for creating training content your way'
          ]
        },
        {
          title: 'Training Marketplace Improvements',
          date: 'Released: November 2025',
          highlights: [
            'Dedicated Marketplace Section — Separated from the store for easier browsing (Nov 28, 2025)',
            'Improved Lesson Management — Better table layouts, thumbnails, and navigation',
            'Enhanced Upload Support — Increased size limits for training materials (Nov 2025)',
            'Team Training Reports — Moved to Reporting menu for better organization'
          ]
        },
        {
          title: 'Training Assignment Improvements',
          date: 'Released: October 2025',
          highlights: [
            'Bulk Training Assignments — Send training to all users across all active accounts (Oct 1, 2025)',
            'Queue Optimization — Fixed overlap issues in training assignment processing (Oct 31, 2025)',
            'User Session Management — Improved handling of signup links and sessions'
          ]
        }
      ]
    },
    {
      icon: Zap,
      title: 'Performance & Infrastructure',
      gradient: 'from-yellow-500 to-orange-600',
      features: [
        {
          title: 'Platform Performance Improvements',
          date: 'Released: Throughout 2024-2025',
          description: 'Major infrastructure upgrades delivering dramatically faster response times across the entire platform. Backend processing is now 3-5x faster, and the control panel loads significantly quicker.'
        },
        {
          title: 'Security & Compliance',
          date: 'Released: Throughout 2024-2025',
          highlights: [
            'SOC2 Compliance Updates — Enhanced security measures and regular audits',
            'Advanced Security Protocols — Multi-layer protection for your data',
            'Rate Limiting Improvements — Better API performance and protection',
            'Regular Security Patches — Continuous updates to maintain platform security'
          ]
        },
        {
          title: 'Reliability & Uptime',
          highlights: [
            'Enhanced Monitoring — Proactive issue detection and automatic recovery',
            'Improved Stability — 99.9% uptime with robust failover systems',
            'Resource Optimization — Faster performance with lower latency',
            'Continuous Improvements — Regular updates for optimal performance'
          ]
        }
      ]
    },
    {
      icon: Target,
      title: 'Quality of Life Improvements',
      gradient: 'from-indigo-500 to-blue-600',
      features: [
        {
          title: 'User Interface Polish',
          date: 'Released: November 2025',
          highlights: [
            'Dark Mode Support — System-wide dark theme for reduced eye strain (Nov 29, 2025)',
            'My Performance Page — Track your personal progress and achievements',
            'Improved Select Components — Better dropdowns and search across the platform',
            'Drag & Drop Fixes — Smoother question reordering in templates'
          ]
        },
        {
          title: 'Plugin & Integration Improvements',
          date: 'Released: March - April 2024',
          highlights: [
            'Redesigned Plugins Page — Cleaner layout with usage tracking',
            'New Integration Partners — Expanded ecosystem support',
            'Webhook Enhancements — Variable tags and improved field handling',
            'API Improvements — Better data handling and response times'
          ]
        },
        {
          title: 'Account & User Management',
          date: 'Released: February 2024',
          highlights: [
            'Annual Plans — New yearly billing options with resource usage tracking (Feb 23, 2024)',
            'User Slot Management — Better tracking of user and storage allocation',
            'Separate Training Users — Distinct management for training-only accounts',
            'Email Change Support — Update user emails directly from settings'
          ]
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 mb-8">
            <Sparkles className="w-5 h-5 text-cyan-400" />
            <span className="text-cyan-400 font-semibold">2024-2025 Release Highlights</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-bold mb-6">
            <span className="text-white">Traain Platform</span>
            <br />
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-500 bg-clip-text text-transparent">
              Major Updates & New Features
            </span>
          </h1>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Discover the latest innovations powered by Traain™. We&apos;re constantly evolving to give you the most advanced training platform available.
          </p>
        </div>
      </section>

      {/* Update Categories */}
      <section className="pb-20 px-6">
        <div className="max-w-7xl mx-auto space-y-24">
          {updateCategories.map((category) => {
            const Icon = category.icon;
            return (
              <div key={category.id} className="relative">
                {/* Category Header */}
                <div className="flex items-center gap-4 mb-12">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${category.gradient} flex items-center justify-center shadow-lg`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold text-white">
                    {category.title}
                  </h2>
                </div>

                {/* Features */}
                <div className="space-y-8">
                  {category.features.map((feature) => (
                    <div
                      key={feature.id || feature.title}
                      className="group relative p-8 rounded-2xl bg-gradient-to-br from-gray-900/80 to-blue-900/20 border border-cyan-500/30 hover:border-cyan-400/60 transition-all duration-300"
                    >
                      {/* Glow effect */}
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/0 to-blue-500/0 group-hover:from-cyan-500/10 group-hover:to-blue-500/10 transition-all duration-300"></div>
                      
                      <div className="relative">
                        <h3 className="text-2xl font-bold text-white mb-2">
                          {feature.title}
                        </h3>
                        
                        {feature.date && (
                          <p className="text-sm text-cyan-400 mb-4 font-semibold italic">
                            {feature.date}
                          </p>
                        )}
                        
                        {feature.description && (
                          <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                            {feature.description}
                          </p>
                        )}
                        
                        {feature.highlights && (
                          <ul className="space-y-3">
                            {feature.highlights.map((highlight, idx) => (
                              <li key={`${feature.id || feature.title}-highlight-${idx}`} className="flex items-start gap-3">
                                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 flex-shrink-0"></div>
                                <span className="text-gray-300">{highlight}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Timeline Summary */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-12 text-center">
            Timeline Summary
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { id: 'q1-2024', quarter: 'Q1 2024', releases: 'Plugin Improvements, Account Management' },
              { id: 'q2-2024', quarter: 'Q2 2024', releases: 'Integration Enhancements, API Updates' },
              { id: 'q3-2024', quarter: 'Q3 2024', releases: 'Performance Upgrades, Security Updates' },
              { id: 'q4-2024', quarter: 'Q4 2024', releases: 'Platform Optimization, Infrastructure' },
              { id: 'q1-2025', quarter: 'Q1 2025', releases: 'Infrastructure Enhancement' },
              { id: 'q2-2025', quarter: 'Q2 2025', releases: 'System Reliability Improvements' },
              { id: 'q3-2025', quarter: 'Q3 2025', releases: 'Role Play AI, Gamification, Real-Time Voice' },
              { id: 'q4-2025', quarter: 'Q4 2025', releases: 'Aurora Dark Theme, AI Thumbnails, Platform Enhancements' }
            ].map((item) => (
              <div
                key={item.id}
                className="p-6 rounded-2xl bg-gradient-to-br from-gray-900/80 to-blue-900/20 border border-cyan-500/30 hover:border-cyan-400/60 transition-all duration-300"
              >
                <h3 className="text-xl font-bold text-cyan-400 mb-3">{item.quarter}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{item.releases}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-gray-400 italic">
              These updates represent our commitment to building the most intelligent, user-friendly training platform. Thank you for being part of the Traain community!
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 relative">
        <div className="max-w-5xl mx-auto text-center">
          <div className="p-12 rounded-3xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-2 border-cyan-500/30">
            <TrendingUp className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Experience the Future of Training
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              These updates are live now. Start transforming your workforce performance with Traain™.
            </p>
            <button 
              onClick={openCalendly}
              className="inline-block px-8 py-4 bg-cyan-500 text-black font-semibold rounded-lg hover:bg-cyan-400 transition-all duration-200 hover:scale-105 text-lg"
            >
              Get Started Today
            </button>
          </div>
        </div>
      </section>

      <Footer />
      
      <CalendlyPopup isOpen={isCalendlyOpen} onClose={closeCalendly} />
    </div>
  );
};

export default UpdatesPage;
